<template>
  <div>
    <div v-if="error" class="state">
      <p>Couldn't load posts right now.</p>
      <button type="button" class="state__retry" @click="load">Try again</button>
    </div>

    <div v-else-if="posts && !posts.length" class="state">
      <p>Nothing here yet — check back soon.</p>
    </div>

    <div v-else :class="['grid', isBooks ? 'grid--books' : 'grid--posts']">
      <template v-if="posts">
        <a
          v-for="post in posts"
          :key="post.external_url + post.title"
          :href="post.external_url"
          class="card">
          <div class="card__media">
            <img :src="post.img_url" :alt="post.title" loading="lazy">
          </div>
          <div class="card__body">
            <time class="card__meta" :datetime="post.posted_date">{{ formatDate(post.posted_date) }}</time>
            <h2 class="card__title">{{ post.title }}</h2>
            <p class="card__text">{{ post.content }}</p>
            <span class="card__cta">
              {{ isBooks ? 'View book' : 'Read article' }} <span class="card__arrow" aria-hidden="true">&rarr;</span>
            </span>
          </div>
        </a>
      </template>
      <template v-else>
        <div v-for="n in 3" :key="n" class="card card--skeleton" aria-hidden="true">
          <div class="card__media"></div>
          <div class="card__body">
            <span class="bar bar--sm"></span>
            <span class="bar bar--lg"></span>
            <span class="bar"></span>
            <span class="bar"></span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getSection, formatDate } from '../api'

export default {
  name: 'PostsPage',
  props: {
    page: { type: Object, required: true }
  },
  data() {
    return {
      posts: null,
      error: false
    }
  },
  computed: {
    isBooks() {
      return this.page.layout === 'books'
    }
  },
  methods: {
    formatDate,
    load() {
      this.error = false
      getSection(this.page.section)
        .then(posts => {
          this.posts = posts
        })
        .catch(error => {
          // eslint-disable-next-line
          console.error(error)
          this.error = true
        })
    }
  },
  created() {
    this.load()
  }
}
</script>

<style scoped>
.grid {
  display: grid;
  gap: 24px;
}
.grid--posts {
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
}
.grid--books {
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
}

/* ── Card ───────────────────────────────────────────── */
.card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background-color: var(--surface);
  box-shadow: var(--shadow-sm);
  color: inherit !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
a.card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
  border-color: #d4d0fb;
}
.card__media {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background-color: #eef1f6;
}
.card__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
}
a.card:hover .card__media img {
  transform: scale(1.04);
}
.card__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 20px 22px 22px;
}
.card__meta {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-subtle);
}
.card__title {
  margin: 8px 0 10px;
  font-size: 1.12rem;
  font-weight: 650;
  line-height: 1.35;
  letter-spacing: -0.01em;
  color: var(--text);
}
.card__text {
  margin: 0 0 18px;
  font-size: 0.92rem;
  line-height: 1.65;
  color: var(--text-muted);
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.card__cta {
  margin-top: auto;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--accent);
}
.card__arrow {
  display: inline-block;
  transition: transform 0.2s ease;
}
a.card:hover .card__arrow {
  transform: translateX(4px);
}

/* Latest post gets a wide, side-by-side feature card */
@media (min-width: 900px) {
  .grid--posts a.card:first-child {
    grid-column: 1 / -1;
    flex-direction: row;
  }
  .grid--posts a.card:first-child .card__media {
    flex: 0 0 56%;
    aspect-ratio: auto;
    min-height: 320px;
  }
  .grid--posts a.card:first-child .card__body {
    justify-content: center;
    padding: 36px 40px;
  }
  .grid--posts a.card:first-child .card__title {
    font-size: 1.6rem;
  }
  .grid--posts a.card:first-child .card__text {
    font-size: 1rem;
    -webkit-line-clamp: 6;
  }
}

/* Book covers: show the whole cover rather than cropping */
.grid--books .card__media {
  aspect-ratio: 4 / 5;
  padding: 24px;
  background: linear-gradient(160deg, #eef1f6, #e3e7f0);
}
.grid--books .card__media img {
  object-fit: contain;
  filter: drop-shadow(0 10px 18px rgba(15, 23, 42, 0.25));
}
.grid--books a.card:hover .card__media img {
  transform: scale(1.03) translateY(-2px);
}
.grid--books .card__title {
  font-size: 1rem;
}
.grid--books .card__text {
  -webkit-line-clamp: 3;
}

/* ── Loading skeleton ───────────────────────────────── */
.card--skeleton .card__media,
.card--skeleton .bar {
  background: linear-gradient(90deg, #eef1f6 25%, #f6f8fb 50%, #eef1f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}
.bar {
  display: block;
  height: 12px;
  margin-bottom: 10px;
  border-radius: 6px;
}
.bar--sm {
  width: 30%;
}
.bar--lg {
  height: 18px;
  width: 80%;
  margin: 6px 0 14px;
}
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Empty / error ──────────────────────────────────── */
.state {
  padding: 48px 24px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background-color: var(--surface);
  text-align: center;
  color: var(--text-muted);
}
.state p {
  margin: 0;
}
.state__retry {
  margin-top: 14px;
  padding: 7px 18px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background-color: var(--surface);
  font-weight: 600;
  color: var(--accent);
}
.state__retry:hover {
  border-color: var(--accent);
}
</style>
